
import Page from '@/Page.vue';
import { Component } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';

import TpmFiltersBar from '@/libs/tpm-analytics/components/TpmFiltersBar.vue';
import MissingCycleTimesAlert from '@/libs/tpm-analytics/components/MissingCycleTimesAlert.vue';
import { getDateRange } from '@/libs/tpm-analytics/core/date-range.utils';
import { resetMissingCycleTimes } from '@/libs/tpm-analytics/core/missing-cycle-times';

import {
    getFinancialImpactSummary,
    getFinancialImpactTrendDaily,
    FinancialImpactTrendPointDay,
    getFinancialCostsByWorkplace,
    FinancialCostsByWorkplaceItem,
    getFinancialCostsTrendByWorkplace,
    FinancialImpactTrendByWorkplace,
} from '@/libs/tpm-analytics/tpm-analytics-data-service';
import {
    getFinancialImpactTopDrivers,
    getFinancialImpactByFaultType,
    getFinancialImpactByShiftTeam,
    getFinancialImpactByShiftType,
    getFinancialImpactByProject,
    getFinancialTimeOfDayHeatmap,
} from '@/libs/tpm-analytics/services/financial-breakdown.service';
import {
    calculateRecurringIssuesCost,
} from '@/libs/tpm-analytics/services/financial-roi.calculator';
import { PlanItemsRepository } from '@/libs/tpm-analytics/data/plan-items.repository';
import { GlobalVariables } from '@/libs/core/global';
import { tenantsStore } from '@/libs/tenants/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { TenantModel } from '@/libs/Api';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import {
    getSelectedFilterLocal,
    setSelectedFilterLocal,
    getFilterById,
    unpackSettings,
} from '@/libs/tpm-analytics/core/saved-filters';
import {
    saveChartZoom,
    loadChartZoom,
    getOptimalWindowSize,
    clearChartZoom,
} from '@/libs/tpm-analytics/core/chart-zoom-storage';
import { getDayStartMinutes, productionDayKey } from '@/libs/tpm-analytics/core/production-day.utils';
@Component({ components: { TpmFiltersBar, MissingCycleTimesAlert } })
export default class TpmAnalyticsFinancialImpact extends Page {
    periodPreset:
        | 'previousDay'
        | 'last7d'
        | 'last14d'
        | 'last30d'
        | 'last3m'
        | 'thisWeek'
        | 'lastWeek'
        | 'thisMonth'
        | 'lastMonth'
        | 'thisQuarter'
        | 'lastQuarter'
        | 'thisYear'
        | 'lastYear'
        | 'custom' = 'last7d';
    customFrom: string | null = null;
    customTo: string | null = null;
    selectedLocationIds: string[] = [];
    selectedWorkplaceIds: string[] = [];
    selectedProjectIds: string[] = [];
    selectedShiftTypeIds: string[] = [];
    selectedShiftTeamTypeIds: string[] = [];
    selectedFaultTypeIds: string[] = [];
    fiGranularity: 'day' | 'week' | 'month' = 'day';
    applyingInitial = false;
    filtersDebounceTimer: any = null;

    // Sdílené uložené filtry – spravuje parent (TpmAnalytics.vue)

    // KPI state
    totalImpactKc = 0;
    downtimeCostsKc = 0;
    maintenanceCostsKc = 0;
    repairCostsKc = 0;
    scrapCostsKc = 0;
    lostProfitKc = 0;
    prevTotalImpactKc = 0;
    prevDowntimeCostsKc = 0;
    prevMaintenanceCostsKc = 0;
    prevRepairCostsKc = 0;
    prevScrapCostsKc = 0;
    prevLostProfitKc = 0;
    scrapDataCompletenessPercent: number | null = null;
    
    // Nové KPI metriky
    avgCostPerDowntime = 0;
    mostExpensiveDayValue = 0;
    mostExpensiveDayDate: string | null = null;
    recurringIssuesCost = 0;

    // Trend data
    trend: FinancialImpactTrendPointDay[] = [];
    viewStart: number | null = null;
    viewEnd: number | null = null;
    // Pro kategorickou osu: indexy viditelných kategorií
    categoryStartIndex: number | null = null;
    categoryEndIndex: number | null = null;

    // By workplace stacked bar
    byWorkplace: FinancialCostsByWorkplaceItem[] = [];
    workplaceTrendData: FinancialImpactTrendByWorkplace[] = [];
    workplaceChartMode: 'trend' | 'average' = 'trend';
    wpViewStart: number | null = null;
    wpViewEnd: number | null = null;
    
    // Breakdown data
    byFaultType: any[] = [];
    byShiftTeam: any[] = [];
    byShiftType: any[] = [];
    byProject: any[] = [];
    topDrivers: any[] = [];
    timeOfDayHeatmap: any = null;

    get windowDays(): number {
        return getOptimalWindowSize(this.fiGranularity);
    }

    get periodItems() {
        return [
            { text: this.$t('tpmFilters.previousDay') as any, value: 'previousDay' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.last7d') as any, value: 'last7d' },
            { text: this.$t('tpmFilters.last14d') as any, value: 'last14d' },
            { text: this.$t('tpmFilters.last30d') as any, value: 'last30d' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.preset7d') as any, value: '7d' },
            { text: this.$t('tpmFilters.preset1m') as any, value: '1m' },
            { text: this.$t('tpmFilters.preset3m') as any, value: '3m' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.thisWeek') as any, value: 'thisWeek' },
            { text: this.$t('tpmFilters.lastWeek') as any, value: 'lastWeek' },
            { text: this.$t('tpmFilters.thisMonth') as any, value: 'thisMonth' },
            { text: this.$t('tpmFilters.lastMonth') as any, value: 'lastMonth' },
            { text: this.$t('tpmFilters.thisQuarter') as any, value: 'thisQuarter' },
            { text: this.$t('tpmFilters.lastQuarter') as any, value: 'lastQuarter' },
            { text: this.$t('tpmFilters.thisYear') as any, value: 'thisYear' },
            { text: this.$t('tpmFilters.lastYear') as any, value: 'lastYear' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.presetCustom') as any, value: 'custom' },
        ];
    }

    getRange() {
        return getDateRange({
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            granularity: this.fiGranularity,
        });
    }

    get currentPeriodText() {
        const { from, to } = this.getRange();
        const fmt = (v: string) => moment.tz(v, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm');
        return `${fmt(from)} – ${fmt(to)}`;
    }
    get previousPeriodText() {
        const { from, to } = this.getRange();
        const spanMin = Math.max(1, moment(to).diff(moment(from), 'minutes'));
        // Předchozí období končí tam, kde začíná aktuální (zobrazení jako exkluzivní konec)
        const prevTo = from;
        const prevFrom = moment(from).subtract(spanMin, 'minutes').toISOString();
        const fmt = (v: string) => moment.tz(v, DISPLAY_TIMEZONE).format('dddd DD.MM.YYYY HH:mm');
        return `${fmt(prevFrom)} – ${fmt(prevTo)}`;
    }

    onFiltersChanged() {
        if (this.applyingInitial) return;
        // Debounce 200ms - zabráníme duplicitnímu volání při rychlé změně více filtrů
        if (this.filtersDebounceTimer) clearTimeout(this.filtersDebounceTimer);
        this.filtersDebounceTimer = setTimeout(() => {
            this.executeFiltersChanged();
        }, 200);
    }

    executeFiltersChanged() {
        // Reset missing cycle times na začátku celého načítání
        resetMissingCycleTimes();
        const { from, to } = this.getRange();
        const filters = {
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            faultTypeIds: this.selectedFaultTypeIds,
        } as any;
        getFinancialImpactSummary(from, to, filters).then((s) => {
            this.totalImpactKc = Math.round(s.totalImpactKc);
            this.downtimeCostsKc = Math.round(s.downtimeCostsKc);
            this.maintenanceCostsKc = Math.round(s.maintenanceCostsKc);
            this.repairCostsKc = Math.round(s.repairCostsKc);
            this.scrapCostsKc = Math.round(s.scrapCostsKc);
            this.lostProfitKc = Math.round(s.lostProfitKc);
            this.prevTotalImpactKc = Math.round(s.prev?.totalImpactKc || 0);
            this.prevDowntimeCostsKc = Math.round(s.prev?.downtimeCostsKc || 0);
            this.prevMaintenanceCostsKc = Math.round(s.prev?.maintenanceCostsKc || 0);
            this.prevRepairCostsKc = Math.round(s.prev?.repairCostsKc || 0);
            this.prevScrapCostsKc = Math.round(s.prev?.scrapCostsKc || 0);
            this.prevLostProfitKc = Math.round(s.prev?.lostProfitKc || 0);
            this.scrapDataCompletenessPercent = s.meta?.scrapDataCompleteness?.completenessPercent ?? null;
        });
        getFinancialImpactTrendDaily(from, to, filters).then((list) => {
            this.trend = list;
            this.resetViewport();
            // Najdeme nejdražší den
            if (list.length > 0) {
                const dayWithMaxCost = list.reduce((max, d) => {
                    const total = (d.downtimeKc || 0) + (d.maintenanceKc || 0) + (d.scrapKc || 0) + (d.lostProfitKc || 0);
                    const maxTotal = (max.downtimeKc || 0) + (max.maintenanceKc || 0) + (max.scrapKc || 0) + (max.lostProfitKc || 0);
                    return total > maxTotal ? d : max;
                });
                this.mostExpensiveDayValue = Math.round((dayWithMaxCost.downtimeKc || 0) + (dayWithMaxCost.maintenanceKc || 0) + (dayWithMaxCost.scrapKc || 0) + (dayWithMaxCost.lostProfitKc || 0));
                this.mostExpensiveDayDate = dayWithMaxCost.day;
            }
        });
        getFinancialCostsByWorkplace(from, to, filters).then((items) => {
            this.byWorkplace = items;
        });
        getFinancialCostsTrendByWorkplace(from, to, this.fiGranularity, filters).then((items) => {
            this.workplaceTrendData = items;
            console.log('[FI Vue] Loaded workplaceTrendData:', {
                count: items.length,
                sample: items[0],
                granularity: this.fiGranularity,
            });
            // Initialize zoom for workplace trend based on total period count
            const { from: rangeFrom, to: rangeTo } = this.getRange();
            if (rangeFrom && rangeTo) {
                const periods: string[] = [];
                const current = moment(rangeFrom);
                const endDate = moment(rangeTo);
                
                if (this.fiGranularity === 'week') {
                    current.startOf('isoWeek');
                    while (current.isSameOrBefore(endDate)) {
                        periods.push(current.format('YYYY-MM-DD'));
                        current.add(1, 'week');
                    }
                } else if (this.fiGranularity === 'month') {
                    current.startOf('month');
                    while (current.isSameOrBefore(endDate)) {
                        periods.push(current.format('YYYY-MM-DD'));
                        current.add(1, 'month');
                    }
                } else {
                    while (current.isSameOrBefore(endDate)) {
                        periods.push(productionDayKey(current.valueOf()));
                        current.add(1, 'day');
                    }
                }
                
                const wpLen = periods.length;
                const optimal = this.windowDays;
                this.wpViewStart = Math.max(0, wpLen - optimal);
                this.wpViewEnd = wpLen;
            }
        });
        // Průměrná cena odstávky
        PlanItemsRepository.fetchShutdownPlanItemsForRange([], from, to, {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
            faultTypeIds: filters?.faultTypeIds,
        }).then((planItems) => {
            if (planItems.length > 0) {
                this.avgCostPerDowntime = Math.round(this.totalImpactKc / planItems.length);
            } else {
                this.avgCostPerDowntime = 0;
            }
                // Opakující se problémy
            const recurring = calculateRecurringIssuesCost(planItems as any[], 3, this.totalImpactKc);
            this.recurringIssuesCost = recurring.totalRecurringCost;
        });
        // Breakdown podle fault types
        getFinancialImpactByFaultType(from, to, filters).then((items) => {
            this.byFaultType = items;
        });
        // Breakdown podle shift teams
        getFinancialImpactByShiftTeam(from, to, filters).then((items) => {
            this.byShiftTeam = items;
        });
        // Breakdown podle shift types
        getFinancialImpactByShiftType(from, to, filters).then((items) => {
            this.byShiftType = items;
        });
        // Breakdown podle projektů
        getFinancialImpactByProject(from, to, filters).then((items) => {
            this.byProject = items;
        });
        // Top cost drivers
        getFinancialImpactTopDrivers(from, to, 20, filters).then((items) => {
            this.topDrivers = items;
        });
        // Time of day heatmap
        getFinancialTimeOfDayHeatmap(from, to, filters).then((result) => {
            this.timeOfDayHeatmap = result;
        });

        const fn = (this.$root as any).$tpmOnStateChanged;
        if (typeof fn === 'function') fn();
    }

    $watch!: any;
    created() {
        // Ensure reactive properties are initialized
        if (!this.workplaceChartMode) {
            this.workplaceChartMode = 'trend';
        }
        
        (this.$root as any).$tpmGetCurrentState = this.getCurrentFilterState.bind(this);
        (this.$root as any).$tpmApplyState = this.applyFilterState.bind(this);
        const initState = (this.$root as any).$tpmInitialState;
        const initSid = (this.$root as any).$tpmInitialSid;
        if (initState && Object.keys(initState || {}).length > 0) {
            this.applyFilterState(initState, false);
            return;
        }
        if (initSid) {
            setSelectedFilterLocal(initSid);
            getFilterById(initSid).then((model) => {
                const state = unpackSettings(model?.settings);
                this.applyFilterState(state, false);
            });
            return;
        }
        const localId = getSelectedFilterLocal();
        if (localId) {
            setSelectedFilterLocal(localId);
            getFilterById(localId).then((model) => {
                const state = unpackSettings(model?.settings);
                this.applyFilterState(state, false);
            });
        }
        // Pokud není žádný initial state, mounted() zavolá load() s defaultními hodnotami
    }

    mounted() {
        // V mounted() VŽDY voláme load() - created() pouze nastavil stav
        this.onFiltersChanged();
        
        // Watch registrujeme až v mounted(), aby se nespustil při async načítání initial state v created()
        this.$watch('fiGranularity', (newVal: 'day' | 'week' | 'month', oldVal: 'day' | 'week' | 'month') => {
            if (oldVal && newVal !== oldVal) {
                // Při změně granularity resetujeme zoom
                this.resetViewport();
            }
            this.onFiltersChanged();
        });
        
        this.$watch('workplaceChartMode', () => {
            this.onFiltersChanged();
            // Update parent state for URL persistence and tab synchronization
            const currentState = this.getCurrentFilterState();
            this.$parent?.$emit('update-state', currentState);
        });
    }

    destroyed() {
        if ((this.$root as any).$tpmGetCurrentState === this.getCurrentFilterState)
            delete (this.$root as any).$tpmGetCurrentState;
        if ((this.$root as any).$tpmApplyState === this.applyFilterState) delete (this.$root as any).$tpmApplyState;
    }

    getCurrentFilterState() {
        return {
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            selectedLocationIds: this.selectedLocationIds,
            selectedWorkplaceIds: this.selectedWorkplaceIds,
            selectedProjectIds: this.selectedProjectIds,
            selectedShiftTypeIds: this.selectedShiftTypeIds,
            selectedShiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            selectedFaultTypeIds: this.selectedFaultTypeIds,
            granularity: this.fiGranularity,
            workplaceChartMode: this.workplaceChartMode,
        } as any;
    }
    applyFilterState(state: any, reload = true) {
        // reload = true: uživatel změnil filtry → načti data
        // reload = false: inicializace v created() → pouze nastav stav, data se načtou v mounted()
        const periodChanged = state?.periodPreset && state.periodPreset !== this.periodPreset;
        const customDatesChanged = (state?.customFrom && state.customFrom !== this.customFrom) || 
                                   (state?.customTo && state.customTo !== this.customTo);
        
        this.applyingInitial = true;
        try {
            if (state?.periodPreset) this.periodPreset = state.periodPreset;
            if (state?.customFrom) this.customFrom = state.customFrom;
            if (state?.customTo) this.customTo = state.customTo;
            this.selectedLocationIds = Array.isArray(state?.selectedLocationIds) ? state.selectedLocationIds : [];
            this.selectedWorkplaceIds = Array.isArray(state?.selectedWorkplaceIds) ? state.selectedWorkplaceIds : [];
            this.selectedProjectIds = Array.isArray(state?.selectedProjectIds) ? state.selectedProjectIds : [];
            this.selectedShiftTypeIds = Array.isArray(state?.selectedShiftTypeIds) ? state.selectedShiftTypeIds : [];
            this.selectedShiftTeamTypeIds = Array.isArray(state?.selectedShiftTeamTypeIds) ? state.selectedShiftTeamTypeIds : [];
            this.selectedFaultTypeIds = Array.isArray(state?.selectedFaultTypeIds) ? state.selectedFaultTypeIds : [];
            if (state?.granularity) {
                this.fiGranularity = state.granularity;
            } else if (state?.fiGranularity) {
                // Zpětná kompatibilita se starými uloženými filtry
                this.fiGranularity = state.fiGranularity;
            }
            this.workplaceChartMode = state?.workplaceChartMode || 'trend';
        } finally {
            this.applyingInitial = false;
        }
        
        // Pokud se změnilo období, vymazat uložený zoom aby se použil nový default (maximum dat)
        if (periodChanged || customDatesChanged) {
            clearChartZoom('fi-trends');
        }
        
        if (reload) {
            this.onFiltersChanged();
        }
    }

    get currencyCode(): string {
        try {
            const tenantId = authStore.useGetter(AuthGetter.GetTenant) as string;
            const tenants = tenantsStore.useGetter(CrudGetter.Data) as TenantModel[];
            const t = tenants?.first
                ? tenants.first((x: any) => x.id === tenantId)
                : (tenants || []).find((x) => x.id === tenantId);
            const code = (t?.currency || '').toString().trim().toUpperCase();
            return code || 'CZK';
        } catch {
            return 'CZK';
        }
    }

    currencyLocale(): string {
        const code = this.currencyCode;
        switch (code) {
            case 'CZK':
                return 'cs-CZ';
            case 'EUR':
                return 'de-DE';
            case 'USD':
                return 'en-US';
            case 'GBP':
                return 'en-GB';
            case 'JPY':
                return 'ja-JP';
            case 'CNY':
                return 'zh-CN';
            case 'CHF':
                return 'de-CH';
            case 'AUD':
                return 'en-AU';
            case 'CAD':
                return 'en-CA';
            case 'SEK':
                return 'sv-SE';
            case 'NOK':
                return 'nb-NO';
            case 'DKK':
                return 'da-DK';
            case 'PLN':
                return 'pl-PL';
            case 'HUF':
                return 'hu-HU';
            case 'INR':
                return 'hi-IN';
            case 'KRW':
                return 'ko-KR';
            case 'HKD':
                return 'zh-HK';
            case 'SGD':
                return 'en-SG';
            case 'NZD':
                return 'en-NZ';
            case 'ZAR':
                return 'en-ZA';
            case 'TRY':
                return 'tr-TR';
            case 'RUB':
                return 'ru-RU';
            case 'AED':
                return 'ar-AE';
            case 'SAR':
                return 'ar-SA';
            case 'ILS':
                return 'he-IL';
            case 'THB':
                return 'th-TH';
            case 'MYR':
                return 'ms-MY';
            case 'IDR':
                return 'id-ID';
            case 'TWD':
                return 'zh-TW';
            case 'VND':
                return 'vi-VN';
            case 'MXN':
                return 'es-MX';
            case 'BRL':
                return 'pt-BR';
            case 'ARS':
                return 'es-AR';
            case 'COP':
                return 'es-CO';
            default:
                return GlobalVariables.lang || 'cs';
        }
    }

    formatCurrency(v: number): string {
        const num = Math.round(Number(v || 0));
        try {
            return new Intl.NumberFormat(this.currencyLocale(), {
                style: 'currency',
                currency: this.currencyCode,
                currencyDisplay: 'symbol',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(num);
        } catch {
            return `${new Intl.NumberFormat(GlobalVariables.lang || 'cs').format(num)} ${this.currencyCode}`;
        }
    }
    formatDelta(pct: number | null): string {
        if (pct == null || !isFinite(pct)) return '—';
        const s = (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%';
        return s;
    }
    get deltaTotalPct() {
        return this.computeDelta(this.totalImpactKc, this.prevTotalImpactKc);
    }
    get deltaDowntimePct() {
        return this.computeDelta(this.downtimeCostsKc, this.prevDowntimeCostsKc);
    }
    get deltaScrapPct() {
        return this.computeDelta(this.scrapCostsKc, this.prevScrapCostsKc);
    }
    get deltaLostPct() {
        return this.computeDelta(this.lostProfitKc, this.prevLostProfitKc);
    }
    get deltaMaintenancePct() {
        return this.computeDelta(this.maintenanceCostsKc + this.repairCostsKc, this.prevMaintenanceCostsKc + this.prevRepairCostsKc);
    }
    computeDelta(cur: number, prev: number): number | null {
        if (!prev) return null;
        return +(((cur - prev) / prev) * 100).toFixed(1);
    }
    formatDateShort(dateStr: string): string {
        return moment(dateStr).format('D.M.YYYY');
    }

    // Agregovaná data pro trend chart (podle granularity)
    get aggregatedDataForTrend(): FinancialImpactTrendPointDay[] {
        if (this.fiGranularity === 'day') {
            return this.trend;
        }

        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;

        const grouped = new Map<string, { downtime: number; maintenance: number; scrap: number; lost: number; count: number }>();

        this.trend.forEach((d) => {
            let periodKey: number;
            if (this.fiGranularity === 'week') {
                periodKey = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else {
                periodKey = moment(d.day).startOf('month').valueOf() + dayStartMs;
            }
            const key = moment(periodKey).toISOString();

            const e = grouped.get(key) || { downtime: 0, maintenance: 0, scrap: 0, lost: 0, count: 0 };
            e.downtime += d.downtimeKc || 0;
            e.maintenance += d.maintenanceKc || 0;
            e.scrap += d.scrapKc || 0;
            e.lost += d.lostProfitKc || 0;
            e.count += 1;
            grouped.set(key, e);
        });

        const aggregated: FinancialImpactTrendPointDay[] = [];
        grouped.forEach((value, key) => {
            aggregated.push({
                day: key,
                downtimeKc: value.downtime,
                maintenanceKc: value.maintenance,
                scrapKc: value.scrap,
                lostProfitKc: value.lost,
            });
        });

        return aggregated.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }

    // Pomocná funkce pro kategorie (společné pro všechny grafy)
    getCategories() {
        const self = this;
        return this.aggregatedDataForTrend.map((d) => {
            const dayStartMinutes = getDayStartMinutes();
            const dayStartMs = dayStartMinutes * 60 * 1000;
            let timestamp: number;
            
            if (this.fiGranularity === 'week') {
                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.fiGranularity === 'month') {
                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('day').valueOf() + dayStartMs;
            }
            
            return self.formatAxisLabel(timestamp, self.fiGranularity);
        });
    }

    // Celkový trend - pouze celkové náklady
    get totalTrendOptions() {
        const self = this;
        const categories = this.getCategories();
        
        return {
            chart: {
                id: 'fi-total-trend',
                type: 'line',
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: true, easing: 'easeinout', speed: 400 },
            },
            dataLabels: { enabled: false },
            stroke: { 
                curve: 'smooth',
                width: 4,
            },
            xaxis: {
                type: 'category',
                categories: categories,
                min: this.categoryStartIndex === 0 ? -0.8 : (this.categoryStartIndex != null ? this.categoryStartIndex - 0.3 : undefined),
                max: this.categoryEndIndex != null ? this.categoryEndIndex + 1.3 : undefined,
                labels: {
                    rotate: self.fiGranularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        if (self.fiGranularity === 'week' && typeof value === 'string' && value.includes('|')) {
                            return value.split('|');
                        }
                        return value;
                    },
                    style: {
                        cssClass: 'apexcharts-xaxis-label-multiline',
                    },
                },
            },
            grid: {
                padding: {
                    right: 5,
                    left: this.categoryStartIndex === 0 ? 0 : 10,
                }
            },
            yaxis: {
                labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) },
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.7,
                    opacityTo: 0.3,
                    stops: [0, 90, 100]
                }
            },
            legend: { show: false },
            markers: { 
                size: 5,
                strokeWidth: 2,
                hover: { size: 7 }
            },
            colors: ['#10b981'],
            tooltip: { 
                shared: true, 
                intersect: false,
                y: { formatter: (val: number) => self.formatCurrency(val) }
            },
        } as any;
    }
    
    get totalTrendSeries() {
        const total = this.aggregatedDataForTrend.map((d) => 
            Math.round((d.downtimeKc || 0) + (d.maintenanceKc || 0) + (d.scrapKc || 0) + (d.lostProfitKc || 0))
        );
        return [{ name: this.$t('tpmAnalytics.fiTotalImpact'), data: total }] as any;
    }

    // Graf pro náklady na odstávky
    get downtimeTrendOptions() {
        const self = this;
        return {
            chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false } },
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth', width: 2 },
            fill: { type: 'gradient', gradient: { opacityFrom: 0.6, opacityTo: 0.1 } },
            xaxis: { 
                type: 'category',
                categories: this.getCategories(),
                labels: { show: true, rotate: -45, style: { fontSize: '10px' } }
            },
            yaxis: { labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) } },
            colors: ['#ef4444'],
            tooltip: { y: { formatter: (val: number) => self.formatCurrency(val) } },
            legend: { show: false },
        } as any;
    }
    
    get downtimeTrendSeries() {
        const data = this.aggregatedDataForTrend.map((d) => Math.round(d.downtimeKc || 0));
        return [{ name: this.$t('tpmAnalytics.fiDowntimeLabel'), data }] as any;
    }

    // Graf pro údržbu
    get maintenanceTrendOptions() {
        const self = this;
        return {
            chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false } },
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth', width: 2 },
            fill: { type: 'gradient', gradient: { opacityFrom: 0.6, opacityTo: 0.1 } },
            xaxis: { 
                type: 'category',
                categories: this.getCategories(),
                labels: { show: true, rotate: -45, style: { fontSize: '10px' } }
            },
            yaxis: { labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) } },
            colors: ['#3b82f6'],
            tooltip: { y: { formatter: (val: number) => self.formatCurrency(val) } },
            legend: { show: false },
        } as any;
    }
    
    get maintenanceTrendSeries() {
        const data = this.aggregatedDataForTrend.map((d) => Math.round(d.maintenanceKc || 0));
        return [{ name: this.$t('tpmAnalytics.maintenanceLabel'), data }] as any;
    }

    // Graf pro zmetky
    get scrapTrendOptions() {
        const self = this;
        return {
            chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false } },
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth', width: 2 },
            fill: { type: 'gradient', gradient: { opacityFrom: 0.6, opacityTo: 0.1 } },
            xaxis: { 
                type: 'category',
                categories: this.getCategories(),
                labels: { show: true, rotate: -45, style: { fontSize: '10px' } }
            },
            yaxis: { labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) } },
            colors: ['#f59e0b'],
            tooltip: { y: { formatter: (val: number) => self.formatCurrency(val) } },
            legend: { show: false },
        } as any;
    }
    
    get scrapTrendSeries() {
        const data = this.aggregatedDataForTrend.map((d) => Math.round(d.scrapKc || 0));
        return [{ name: this.$t('tpmAnalytics.fiScrapLabel'), data }] as any;
    }

    // Graf pro ušlý zisk
    get lostProfitTrendOptions() {
        const self = this;
        return {
            chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false } },
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth', width: 2 },
            fill: { type: 'gradient', gradient: { opacityFrom: 0.6, opacityTo: 0.1 } },
            xaxis: { 
                type: 'category',
                categories: this.getCategories(),
                labels: { show: true, rotate: -45, style: { fontSize: '10px' } }
            },
            yaxis: { labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) } },
            colors: ['#8b5cf6'],
            tooltip: { y: { formatter: (val: number) => self.formatCurrency(val) } },
            legend: { show: false },
        } as any;
    }
    
    get lostProfitTrendSeries() {
        const data = this.aggregatedDataForTrend.map((d) => Math.round(d.lostProfitKc || 0));
        return [{ name: this.$t('tpmAnalytics.fiLostLabel'), data }] as any;
    }
    formatDay(ts: number): string {
        return moment.tz(ts, DISPLAY_TIMEZONE).format('D.M.');
    }
    formatAxisLabel(ts: number, gran: 'day' | 'week' | 'month'): string {
        const m = moment.tz(ts, DISPLAY_TIMEZONE);
        if (!m.isValid()) return '';
        if (gran === 'month') return m.format('MM/YYYY');
        if (gran === 'week') {
            const start = m.clone().startOf('isoWeek');
            const end = m.clone().endOf('isoWeek');
            const week = m.isoWeek();
            const weekLabel = `${week}. týden`;
            const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
            return weekLabel + '|' + dateRange;
        }
        return m.format('D.M.');
    }
    resetViewport() {
        const data = this.aggregatedDataForTrend;
        if (data.length === 0) {
            this.categoryStartIndex = null;
            this.categoryEndIndex = null;
            return;
        }

        // Pro kategorickou osu: zobrazíme všechna data (null = všechno)
        this.categoryStartIndex = null;
        this.categoryEndIndex = null;
    }
    shiftPercent(percent: number) {
        const data = this.aggregatedDataForTrend;
        if (data.length === 0) return;

        let startIdx: number;
        let endIdx: number;

        if (this.categoryStartIndex == null || this.categoryEndIndex == null) {
            const windowSize = Math.min(10, data.length);
            if (data.length <= windowSize) return;

            if (percent > 0) {
                startIdx = 0;
                endIdx = windowSize - 1;
            } else {
                startIdx = data.length - windowSize;
                endIdx = data.length - 1;
            }
        } else {
            startIdx = this.categoryStartIndex;
            endIdx = this.categoryEndIndex;
        }

        const visibleCount = endIdx - startIdx + 1;
        const calculatedShift = visibleCount * percent;
        const shift = percent > 0 ? Math.max(1, Math.round(calculatedShift)) : Math.min(-1, Math.round(calculatedShift));

        let newStart = startIdx + shift;
        let newEnd = endIdx + shift;

        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + visibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = newEnd - visibleCount + 1;
            if (newStart < 0) {
                newStart = 0;
                newEnd = data.length - 1;
            }
        }

        if (newStart === startIdx && newEnd === endIdx) return;

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
    }
    zoomIn() {
        const data = this.aggregatedDataForTrend;
        if (data.length === 0) return;

        const startIdx = this.categoryStartIndex ?? 0;
        const endIdx = this.categoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;
        
        const minVisible = 2;
        const newVisibleCount = Math.max(minVisible, Math.round(visibleCount * 0.75));
        
        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
    }
    zoomOut() {
        const data = this.aggregatedDataForTrend;
        if (data.length === 0) return;

        const startIdx = this.categoryStartIndex ?? 0;
        const endIdx = this.categoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;
        
        const maxVisible = data.length;
        const newVisibleCount = Math.min(maxVisible, Math.round(visibleCount / 0.75));

        if (newVisibleCount >= data.length) {
            this.categoryStartIndex = null;
            this.categoryEndIndex = null;
            return;
        }
        
        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
    }

    saveCurrentZoom() {
        if (this.viewStart != null && this.viewEnd != null && this.trend.length > 0) {
            const unit = this.fiGranularity === 'week' ? 'isoWeek' : this.fiGranularity === 'month' ? 'month' : 'day';
            const dataStart = moment(this.trend[0].day).startOf(unit as any).valueOf();
            const dataEnd = moment(this.trend[this.trend.length - 1].day).endOf(unit as any).valueOf();
            saveChartZoom('fi-trend', this.fiGranularity, this.viewStart, this.viewEnd, dataStart, dataEnd, unit);
        }
    }

    // Workplace trend zoom functions
    wpZoomIn(): void {
        if (this.wpViewStart == null || this.wpViewEnd == null) return;
        const maxLen = this.getWorkplacePeriodCount();
        const range = this.wpViewEnd - this.wpViewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));
        const mid = Math.floor((this.wpViewStart + this.wpViewEnd) / 2);
        this.wpViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.wpViewEnd = Math.min(maxLen, this.wpViewStart + newRange);
    }

    wpZoomOut(): void {
        if (this.wpViewStart == null || this.wpViewEnd == null) return;
        const maxLen = this.getWorkplacePeriodCount();
        const range = this.wpViewEnd - this.wpViewStart;
        const newRange = Math.min(maxLen, Math.round(range * 1.5));
        const mid = Math.floor((this.wpViewStart + this.wpViewEnd) / 2);
        this.wpViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.wpViewEnd = Math.min(maxLen, this.wpViewStart + newRange);
    }

    wpShiftPercent(pct: number): void {
        if (this.wpViewStart == null || this.wpViewEnd == null) return;
        const maxLen = this.getWorkplacePeriodCount();
        const range = this.wpViewEnd - this.wpViewStart;
        const shift = Math.round(range * pct);
        this.wpViewStart = Math.max(0, Math.min(maxLen - range, this.wpViewStart + shift));
        this.wpViewEnd = this.wpViewStart + range;
    }

    getWorkplacePeriodCount(): number {
        const { from, to } = this.getRange();
        if (!from || !to) return 0;
        
        let count = 0;
        const current = moment(from);
        const endDate = moment(to);
        
        if (this.fiGranularity === 'week') {
            current.startOf('isoWeek');
            while (current.isSameOrBefore(endDate)) {
                count++;
                current.add(1, 'week');
            }
        } else if (this.fiGranularity === 'month') {
            current.startOf('month');
            while (current.isSameOrBefore(endDate)) {
                count++;
                current.add(1, 'month');
            }
        } else {
            count = endDate.diff(current, 'days') + 1;
        }
        
        return count;
    }

    // Workplace chart options & series
    get workplaceTrendDataVisible(): FinancialImpactTrendPointDay[] {
        if (this.workplaceChartMode !== 'trend') return [];
        
        // Generate complete list of periods based on date range and granularity
        const { from, to } = this.getRange();
        if (!from || !to) return [];
        
        const periods: string[] = [];
        const current = moment(from);
        const endDate = moment(to);
        
        if (this.fiGranularity === 'week') {
            // Generate weeks
            current.startOf('isoWeek');
            while (current.isSameOrBefore(endDate)) {
                periods.push(current.format('YYYY-MM-DD'));
                current.add(1, 'week');
            }
        } else if (this.fiGranularity === 'month') {
            // Generate months
            current.startOf('month');
            while (current.isSameOrBefore(endDate)) {
                periods.push(current.format('YYYY-MM-DD'));
                current.add(1, 'month');
            }
        } else {
            // Generate days using productionDayKey for consistency with service
            while (current.isSameOrBefore(endDate)) {
                periods.push(productionDayKey(current.valueOf()));
                current.add(1, 'day');
            }
        }
        
        if (periods.length === 0) return [];
        
        // Apply zoom/pan
        if (this.wpViewStart == null || this.wpViewEnd == null) {
            return periods.map(day => ({ day, downtimeKc: 0, maintenanceKc: 0, scrapKc: 0, lostProfitKc: 0 }));
        }
        
        const start = Math.max(0, Math.min(periods.length - 1, this.wpViewStart));
        const end = Math.max(start, Math.min(periods.length - 1, this.wpViewEnd));
        return periods.slice(start, end + 1).map(day => ({ day, downtimeKc: 0, maintenanceKc: 0, scrapKc: 0, lostProfitKc: 0 }));
    }

    get workplaceChartHeight(): number {
        if (this.workplaceChartMode === 'trend') {
            return 520;
        }
        return 500;
    }

    get workplaceChartOptions() {
        const self = this;
        
        if (this.workplaceChartMode === 'trend') {
            // Trend mode: X = time periods, Y = Financial costs for each workplace
            const categories = this.workplaceTrendDataVisible.map((d) => {
                if (!d.day) return 'N/A';
                const m = moment(d.day);
                if (!m.isValid()) return 'N/A';
                
                if (this.fiGranularity === 'day') {
                    return m.format('D.M.');
                } else if (this.fiGranularity === 'week') {
                    const start = m.clone().startOf('isoWeek');
                    const end = m.clone().endOf('isoWeek');
                    const week = m.isoWeek();
                    const weekLabel = `${week}. týden`;
                    const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
                    return weekLabel + '|' + dateRange;
                } else {
                    return m.format('MM/YYYY');
                }
            });
            
            const shouldRotate = this.fiGranularity === 'week' || categories.length > 8;
            
            return {
                chart: {
                    id: 'financial-workplace-trend',
                    type: 'bar',
                    stacked: false,
                    toolbar: { show: false },
                    zoom: { enabled: false },
                    animations: { enabled: false },
                },
                plotOptions: {
                    bar: {
                        columnWidth: '80%',
                        dataLabels: {
                            position: 'top',
                        },
                    },
                },
                xaxis: {
                    categories,
                    labels: {
                        rotate: shouldRotate ? -45 : 0,
                        rotateAlways: shouldRotate,
                        hideOverlappingLabels: true,
                        trim: true,
                        minHeight: shouldRotate ? 60 : undefined,
                        maxHeight: shouldRotate ? 80 : undefined,
                        style: {
                            cssClass: 'apexcharts-xaxis-label-multiline',
                        },
                        formatter(value: any) {
                            if (typeof value === 'string' && value.includes('|')) {
                                const parts = value.split('|');
                                return parts;
                            }
                            return value;
                        },
                    },
                },
                yaxis: {
                    title: { text: 'Náklady (Kč)' },
                    labels: {
                        formatter: (val: number) => self.formatCurrency(val),
                    },
                },
                dataLabels: { enabled: false },
                legend: { position: 'top' },
                tooltip: {
                    shared: true,
                    intersect: false,
                    followCursor: true,
                    y: {
                        formatter: (val: number) => self.formatCurrency(val),
                    },
                },
                colors: ['#0284c7', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'],
            } as any;
        } else {
            // Average mode: horizontal stacked bar chart
            return {
                chart: { type: 'bar', stacked: true, toolbar: { show: false } },
                plotOptions: { bar: { horizontal: true } },
                xaxis: { labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) } },
                yaxis: { categories: this.byWorkplace.map((w) => w.workplaceName) },
                legend: { position: 'top' },
                tooltip: { y: { formatter: (val: number) => self.formatCurrency(val) } },
            } as any;
        }
    }
    
    get workplaceChartSeries() {
        if (this.workplaceChartMode === 'trend') {
            // Trend mode: series per workplace showing total costs over time
            if (!this.workplaceTrendDataVisible || this.workplaceTrendDataVisible.length === 0) {
                return [] as any;
            }
            if (!this.workplaceTrendData || this.workplaceTrendData.length === 0) {
                return [] as any;
            }
            
            const visiblePeriods = this.workplaceTrendDataVisible.map(d => d.day).filter(d => d);
            console.log('[FI Vue] workplaceChartSeries:', {
                visiblePeriodsCount: visiblePeriods.length,
                sampleVisiblePeriod: visiblePeriods[0],
                workplaceTrendDataCount: this.workplaceTrendData.length,
                sampleWorkplaceTrendData: this.workplaceTrendData[0],
                granularity: this.fiGranularity,
            });
            
            // Create a series for each workplace
            return this.workplaceTrendData.map((wp, wpIndex) => {
                const data: number[] = [];
                let matchedCount = 0;
                
                for (const period of visiblePeriods) {
                    // Find matching point - need to compare based on granularity
                    let point = null;
                    
                    if (wp.points) {
                        if (this.fiGranularity === 'week') {
                            // Compare week starts
                            const periodWeekStart = moment(period).startOf('isoWeek').format('YYYY-MM-DD');
                            point = wp.points.find(p => {
                                const pWeekStart = moment(p.day).startOf('isoWeek').format('YYYY-MM-DD');
                                return pWeekStart === periodWeekStart;
                            });
                        } else if (this.fiGranularity === 'month') {
                            // Compare month starts
                            const periodMonthStart = moment(period).startOf('month').format('YYYY-MM-DD');
                            point = wp.points.find(p => {
                                const pMonthStart = moment(p.day).startOf('month').format('YYYY-MM-DD');
                                return pMonthStart === periodMonthStart;
                            });
                        } else {
                            // Exact day match
                            point = wp.points.find(p => p.day === period);
                        }
                    }
                    
                    if (point) {
                        // Total costs for this workplace on this period
                        const total = (point.downtimeKc || 0) + (point.maintenanceKc || 0) + 
                                     (point.scrapKc || 0) + (point.lostProfitKc || 0);
                        data.push(total);
                        matchedCount++;
                    } else {
                        data.push(0);
                    }
                }
                
                if (wpIndex === 0) {
                    console.log('[FI Vue] First workplace series:', {
                        wpName: wp.workplaceName,
                        totalPoints: wp.points?.length,
                        visiblePeriods: visiblePeriods.length,
                        matchedCount,
                        samplePoint: wp.points?.[0],
                        samplePeriod: visiblePeriods[0],
                        data: data.slice(0, 3),
                    });
                }
                
                return {
                    name: wp.workplaceName || 'N/A',
                    type: 'column',
                    data: data,
                };
            }) as any;
        } else {
            // Average mode: single horizontal bar series per workplace
            if (!this.byWorkplace || this.byWorkplace.length === 0) {
                return [] as any;
            }
            
            return [
                { name: this.$t('tpmAnalytics.fiDowntimeLabel'), data: this.byWorkplace.map((w) => w.downtimeKc || 0), color: '#ef4444' },
                { name: this.$t('tpmAnalytics.maintenanceLabel'), data: this.byWorkplace.map((w) => (w.maintenanceKc || 0) + (w.repairKc || 0)), color: '#3b82f6' },
                { name: this.$t('tpmAnalytics.fiScrapLabel'), data: this.byWorkplace.map((w) => w.scrapKc || 0), color: '#f59e0b' },
            ] as any;
        }
    }

    // Fault type chart options & series
    get faultTypeChartOptions() {
        const self = this;
        return {
            chart: { type: 'bar', stacked: true, toolbar: { show: false } },
            plotOptions: { bar: { horizontal: false } },
            xaxis: { categories: this.byFaultType.slice(0, 10).map((f: any) => f.faultTypeName) },
            yaxis: { labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) } },
            legend: { position: 'top' },
            tooltip: { y: { formatter: (val: number) => self.formatCurrency(val) } },
        } as any;
    }
    get faultTypeChartSeries() {
        const top10 = this.byFaultType.slice(0, 10);
        return [
            { name: this.$t('tpmAnalytics.fiDowntimeLabel'), data: top10.map((f: any) => f.downtimeKc), color: '#ef4444' },
            { name: this.$t('tpmAnalytics.maintenanceLabel'), data: top10.map((f: any) => f.maintenanceKc), color: '#3b82f6' },
            { name: this.$t('tpmAnalytics.fiScrapLabel'), data: top10.map((f: any) => f.scrapKc), color: '#f59e0b' },
            { name: this.$t('tpmAnalytics.fiLostLabel'), data: top10.map((f: any) => f.lostProfitKc), color: '#8b5cf6' },
        ] as any;
    }

    // Shift team chart options & series
    get shiftTeamChartOptions() {
        const self = this;
        return {
            chart: { type: 'bar', stacked: true, toolbar: { show: false }, height: 280 },
            plotOptions: { bar: { horizontal: true } },
            xaxis: { labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) } },
            yaxis: { categories: this.byShiftTeam.map((s: any) => s.shiftTeamTypeName) },
            legend: { show: false },
            tooltip: { y: { formatter: (val: number) => self.formatCurrency(val) } },
        } as any;
    }
    get shiftTeamChartSeries() {
        return [
            { name: this.$t('tpmAnalytics.fiDowntimeLabel'), data: this.byShiftTeam.map((s: any) => s.downtimeKc), color: '#ef4444' },
            { name: this.$t('tpmAnalytics.maintenanceLabel'), data: this.byShiftTeam.map((s: any) => s.maintenanceKc), color: '#3b82f6' },
            { name: this.$t('tpmAnalytics.fiScrapLabel'), data: this.byShiftTeam.map((s: any) => s.scrapKc), color: '#f59e0b' },
        ] as any;
    }

    // Shift type chart options & series
    get shiftTypeChartOptions() {
        const self = this;
        return {
            chart: { type: 'bar', stacked: true, toolbar: { show: false }, height: 280 },
            plotOptions: { bar: { horizontal: true } },
            xaxis: { labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) } },
            yaxis: { categories: this.byShiftType.map((s: any) => s.shiftTypeName) },
            legend: { show: false },
            tooltip: { y: { formatter: (val: number) => self.formatCurrency(val) } },
        } as any;
    }
    get shiftTypeChartSeries() {
        return [
            { name: this.$t('tpmAnalytics.fiDowntimeLabel'), data: this.byShiftType.map((s: any) => s.downtimeKc), color: '#ef4444' },
            { name: this.$t('tpmAnalytics.maintenanceLabel'), data: this.byShiftType.map((s: any) => s.maintenanceKc), color: '#3b82f6' },
            { name: this.$t('tpmAnalytics.fiScrapLabel'), data: this.byShiftType.map((s: any) => s.scrapKc), color: '#f59e0b' },
        ] as any;
    }

    // Project chart options & series
    get projectChartOptions() {
        const self = this;
        return {
            chart: { type: 'bar', stacked: true, toolbar: { show: false }, height: 280 },
            plotOptions: { bar: { horizontal: true } },
            xaxis: { labels: { formatter: (v: any) => self.formatCurrency(Number(v || 0)) } },
            yaxis: { categories: this.byProject.slice(0, 5).map((p: any) => p.projectName) },
            legend: { show: false },
            tooltip: { y: { formatter: (val: number) => self.formatCurrency(val) } },
        } as any;
    }
    get projectChartSeries() {
        const top5 = this.byProject.slice(0, 5);
        return [
            { name: this.$t('tpmAnalytics.fiDowntimeLabel'), data: top5.map((p: any) => p.downtimeKc), color: '#ef4444' },
            { name: this.$t('tpmAnalytics.maintenanceLabel'), data: top5.map((p: any) => p.maintenanceKc), color: '#3b82f6' },
            { name: this.$t('tpmAnalytics.fiScrapLabel'), data: top5.map((p: any) => p.scrapKc), color: '#f59e0b' },
        ] as any;
    }

    // Time heatmap options & series
    get timeHeatmapOptions() {
        return {
            chart: { type: 'heatmap', toolbar: { show: false }, height: 280 },
            plotOptions: { heatmap: { colorScale: { ranges: [{ from: 0, to: 1000, color: '#90EE90' }, { from: 1001, to: 10000, color: '#FFD700' }, { from: 10001, to: 100000, color: '#FF6347' }] } } },
            dataLabels: { enabled: false },
            xaxis: { categories: Array.from({ length: 24 }, (_, i) => `${i}:00`) },
        } as any;
    }
    get timeHeatmapSeries() {
        if (!this.timeOfDayHeatmap || !this.timeOfDayHeatmap.grid) return [];
        const days = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
        return days.map((day, dayIdx) => ({
            name: day,
            data: this.timeOfDayHeatmap.grid[dayIdx].map((v: number, hourIdx: number) => ({ x: `${hourIdx}:00`, y: v })),
        }));
    }
}
